// src/components/FormattedText.js
// ---------------------------------------------------------------------------
// Shows a description that uses ((b)) ((i)) ((r)) ((-)) the way the proposal
// pages do: bold, italic, regular and bullet lines.
//
//   <FormattedText text={job.jobDescription} />
//   <FormattedText text={proposal.jobDescription} untagged="bullet" />
//
// untagged: how lines WITHOUT a tag show.
//   "plain"  (default) - a normal line. Used for jobs, calendar, invoices.
//   "bullet"           - a bullet. Proposal pages have always done this.
//
// Everything renders as <span style="display:block">, so it is safe inside
// the <p> wrappers several pages already put around the description.
// ---------------------------------------------------------------------------

import React from "react";
import { parseFormattedLines } from "../utils/formattedText";

const line = { display: "block", lineHeight: 1.5, marginBottom: 4 };
const bullet = { ...line, paddingLeft: 16, textIndent: -10 };

export const renderFormattedText = (text, { untagged = "plain", fallback = null } = {}) => {
  const lines = parseFormattedLines(text);
  if (!lines.length) return fallback;

  return lines.map((item, index) => {
    if (item.type === "blank") {
      return <span key={index} style={{ display: "block", height: 8 }} />;
    }
    if (item.type === "bold") {
      return (
        <span key={index} className="job-bold" style={{ ...line, fontWeight: 700 }}>
          {item.text}
        </span>
      );
    }
    if (item.type === "italic") {
      return (
        <span key={index} className="job-italic" style={{ ...line, fontStyle: "italic" }}>
          {item.text}
        </span>
      );
    }
    if (item.type === "bullet" || (item.type === "plain" && untagged === "bullet")) {
      return (
        <span key={index} className="job-bullet" style={bullet}>
          • {item.text}
        </span>
      );
    }
    return (
      <span key={index} style={line}>
        {item.text}
      </span>
    );
  });
};

const FormattedText = ({ text, untagged = "plain", fallback = null, className, style }) => {
  const content = renderFormattedText(text, { untagged, fallback });
  if (content === null) return null;
  return (
    <span className={className} style={{ display: "block", whiteSpace: "normal", ...style }}>
      {content}
    </span>
  );
};

export default FormattedText;
